import React, { useState, useEffect, Fragment, useRef } from 'react';
import { coins, certLookup, certificationServices, transactions, tags } from '../utils/api';
import { customFields } from '../utils/api/customFields';
import TransactionPromptModal from './TransactionPromptModal';
import SaleTransactionModal from './SaleTransactionModal';
import CertUpdateModal from './CertUpdateModal';
import TransactionUpdateWarningModal from './TransactionUpdateWarningModal';
import { Dialog, Transition } from '@headlessui/react';
import TagSelector from './TagSelector';
import SellerSelect from './SellerSelect';
import LocationSelect from './LocationSelect';
import CustomFieldsFormSection from './CustomFields/CustomFieldsFormSection';
import NotesSection from './CoinForm/components/NotesSection';
import FormSection from './CoinForm/components/FormSection';


const EditCoinForm = ({ coinId, onSubmit, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [possibleStatuses, setPossibleStatuses] = useState([]);
  const [services, setServices] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showTransactionPrompt, setShowTransactionPrompt] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showDeleteTransactionPrompt, setShowDeleteTransactionPrompt] = useState(false);
  const [showCertUpdateModal, setShowCertUpdateModal] = useState(false);
  const [showTransactionUpdateWarning, setShowTransactionUpdateWarning] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [pendingCertUpdate, setPendingCertUpdate] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);
  const [customFieldValues, setCustomFieldValues] = useState({});

  // Fetch coin data when component mounts
  useEffect(() => {
    const fetchCoinData = async () => {
      try {
        const data = await coins.getById(coinId);
        // Set initial form data
        const initialData = {
          ...formData,
          // Convert values appropriately
          ...Object.entries(data).reduce((acc, [key, value]) => {
            // For population fields, convert null to 0
            if (['population_when_bought', 'current_population', 'pop_higher_when_bought', 'current_pop_higher'].includes(key)) {
              acc[key] = value === null || value === undefined ? 0 : value;
            }
            // For storage location, ensure it's initialized
            else if (key === 'storage_location') {
              acc[key] = value === null || value === undefined ? '' : value;
            }
            // For other fields, convert null/undefined to empty string
            else {
              acc[key] = value === null || value === undefined ? '' : value;
            }
            return acc;
          }, {}),
          status_name: data.status_name || '',
          purchase_date: data.purchase_date ? new Date(data.purchase_date).toISOString().split('T')[0] : '',
          tags: data.tags || [],
          purchase_transaction_id: data.purchase_transaction_id || '',
          // Ensure cac_sticker is a valid enum value
          cac_sticker: ['green', 'gold', 'none'].includes(data.cac_sticker) ? data.cac_sticker : 'none',
        };
        setFormData(initialData);
        setInitialFormData(initialData);

        // If there's a purchase transaction, fetch its details
        if (data.purchase_transaction_id) {
          const transactionData = await transactions.getById(data.purchase_transaction_id);
          const updatedData = {
            ...initialData,
            contact_id: transactionData.contact_id
          };
          setFormData(updatedData);
          setInitialFormData(updatedData);
        }
      } catch (err) {
        console.error('Failed to fetch coin data:', err);
        setError('Failed to load coin data');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchCoinData();
  }, [coinId]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await certificationServices.getAll();
        setServices(data);
      } catch (err) {
        console.error('Failed to fetch certification services:', err);
        setError('Failed to load certification services');
      }
    };
    fetchServices();
  }, []);

  const isLookupDisabled = () => {
    // Allow lookup if there's a PCGS number, even without cert number
    if (formData.pcgs_number) {
      return false;
    }
    // Otherwise require certification service and number
    if (!formData.certification_service_id || !formData.certification_number) {
      return true;
    }
    return false;
  };

  const applyLookupUpdate = async (response, isPCGSNumber, selectedChanges) => {
    try {
      // First update form data with non-image changes
      setFormData(prev => {
        const newData = {
          ...prev,
          // Preserve fields that shouldn't be overwritten
          user_coin_id: prev.user_coin_id,
          // Clear certification service if it's a PCGS number lookup (uncertified coin)
          certification_service_id: isPCGSNumber ? '' : prev.certification_service_id,
          // Clear certification number if it's a PCGS number lookup
          certification_number: isPCGSNumber ? '' : prev.certification_number,
          
          // Apply only selected changes
          ...selectedChanges.reduce((acc, change) => {
            // Handle population fields specifically
            if (change.field === 'current_population') {
              acc[change.field] = response.population;
            } else if (change.field === 'current_pop_higher') {
              acc[change.field] = response.pop_higher;
            } else if (change.field === 'cac_sticker') {
              // Convert CAC sticker object to type string
              acc[change.field] = response.cac_sticker?.type?.toLowerCase() || 'none';
            } else {
              acc[change.field] = response[change.field];
            }
            return acc;
          }, {}),
          
          // Always update these fields as they are current values
          current_population: response.population,
          current_pop_higher: response.pop_higher,
          price_guide_value: response.price_guide_value,
          
          // Set PCGS number if applicable
          ...(isPCGSNumber && { pcgs_number: response.pcgs_number || prev.certification_number })
        };

        return newData;
      });

      // Then handle cert images if available
      if (response.images && response.images.length > 0) {
        const { fetchCertImages } = await import('../utils/certImages');
        const certImages = await fetchCertImages(
          formData.certification_number,
          formData.certification_service_id,
          formData.grade,
          response // Pass the lookup response as existing cert info
        );

        if (certImages.length > 0) {
          setFormData(prev => ({
            ...prev,
            images: certImages
          }));
        }
      }
    } catch (err) {
      console.error('Failed to apply lookup update:', err);
      setError('Failed to update coin details. Please try again.');
    }
  };

  const handleLookup = async () => {
    setLookupLoading(true);
    setError(null);

    try {
      // Check if we're doing a PCGS number lookup
      const isPCGSNumberLookup = formData.pcgs_number && !formData.certification_number;

      // Only require certification service if not doing a PCGS number lookup
      if (!isPCGSNumberLookup) {
        if (!formData.certification_service_id) {
          setError('Please select a certification service');
          setLookupLoading(false);
          return;
        }

        // Get certification service name from services array
        const selectedService = services.find(s => s.id === parseInt(formData.certification_service_id));
        if (!selectedService) {
          throw new Error('Invalid certification service');
        }
      }

      // For PCGS service with cert number, check if it looks like a PCGS number
      const isPCGSCertNumber = !isPCGSNumberLookup && 
        services.find(s => s.id === parseInt(formData.certification_service_id))?.name.toLowerCase() === 'pcgs' && 
        /^\d{1,6}$/.test(formData.certification_number);

      // Get certification service name
      const selectedService = services.find(s => s.id === parseInt(formData.certification_service_id));
      const serviceName = selectedService?.name.toLowerCase() || 'pcgs';

      // Use the certLookup API
      const response = await certLookup.lookup(
        serviceName,
        isPCGSNumberLookup ? formData.pcgs_number : formData.certification_number,
        formData.grade,
        isPCGSNumberLookup || isPCGSCertNumber
      );

      // Get all fields from the parser
      const fieldsToCheck = [
        'variety', 'metal', 'grade', 'mint_mark', 'country', 'denomination',
        'year', 'edge', 'composition', 'weight', 'diameter', 'mintage',
        'minor_variety', 'die_variety', 'reference_number', 'assayer',
        'pcgs_number', 'price_guide_value', 'cac_sticker'
      ];

      // Create changes array with all fields, including unchanged ones
      const changedFields = [];

      // Helper function to check if a value is effectively empty
      const isEmpty = (value) => {
        return value === null || value === undefined || value === '' || value === 0;
      };

      // Helper function to normalize values for comparison
      const normalizeValue = (value, field) => {
        if (isEmpty(value)) return null;
        
        if (['weight', 'diameter', 'composition'].includes(field)) {
          return Number(Number(value).toFixed(2));
        }
        if (['mintage', 'population', 'pop_higher', 'current_population', 'current_pop_higher'].includes(field)) {
          return Number(value);
        }
        if (['price_guide_value'].includes(field)) {
          return Number(Number(value).toFixed(2));
        }
        return value;
      };

      // Helper function to check if values are different
      const hasValueChanged = (oldValue, newValue, field) => {
        // If both values are empty, they're effectively the same
        if (isEmpty(oldValue) && isEmpty(newValue)) {
          return false;
        }
        
        // Special handling for CAC sticker
        if (field === 'cac_sticker') {
          // Convert CAC sticker object to lowercase type for comparison
          const oldType = typeof oldValue === 'object' ? oldValue.type?.toLowerCase() : oldValue?.toLowerCase();
          const newType = typeof newValue === 'object' ? newValue.type?.toLowerCase() : newValue?.toLowerCase();
          // Don't show change if both are effectively 'none'
          if ((oldType === 'none' || !oldType) && (newType === 'none' || !newType)) {
            return false;
          }
          return oldType !== newType;
        }
        
        const normalizedOld = normalizeValue(oldValue, field);
        const normalizedNew = normalizeValue(newValue, field);
        return normalizedOld !== normalizedNew;
      };

      // Add standard fields
      fieldsToCheck.forEach(field => {
        let newValue = response[field];
        // Convert CAC sticker object to type string for display
        if (field === 'cac_sticker' && typeof newValue === 'object') {
          newValue = newValue.type?.toLowerCase() || 'none';
        }
        changedFields.push({
          field,
          oldValue: formData[field],
          newValue,
          hasChanged: hasValueChanged(formData[field], response[field], field)
        });
      });

      // Add population fields with special mapping
      changedFields.push({
        field: 'current_population',
        oldValue: formData.current_population,
        newValue: response.population,
        hasChanged: hasValueChanged(formData.current_population, response.population, 'current_population')
      });
      changedFields.push({
        field: 'current_pop_higher',
        oldValue: formData.current_pop_higher,
        newValue: response.pop_higher,
        hasChanged: hasValueChanged(formData.current_pop_higher, response.pop_higher, 'current_pop_higher')
      });

      // Always show the modal if there are any changes
      if (changedFields.length > 0) {
        setPendingCertUpdate({
          response,
          changedFields,
          isPCGSNumber: isPCGSNumberLookup || isPCGSCertNumber
        });
        setShowCertUpdateModal(true);
        setLookupLoading(false);
        return;
      }

      // If no changes, just update the always-fresh fields
      applyLookupUpdate(response, isPCGSNumberLookup || isPCGSCertNumber, []);
    } catch (err) {
      console.error('Lookup error:', err);
      const errorData = err.response?.data;
      const errorMessage = errorData?.error 
        ? `${errorData.error}${errorData.details ? `: ${errorData.details}` : ''}`
        : 'Failed to fetch coin details. Please try again or enter details manually.';
      setError(errorMessage);
    } finally {
      setLookupLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    user_coin_id: '',
    certification_service_id: '',
    certification_number: '',
    country: '',
    denomination: '',
    year: '',
    mint_mark: '',
    contact_id: '',
    variety: '',
    ngc_grade: '',
    grade: '',
    metal: '',
    composition: '',
    weight: '',
    diameter: '',
    edge: '',
    storage_location: '',
    reference_number: '',
    assayer: '',
    provenance: '',
    coin_details: '',
    cac_sticker: 'none',
    purchase_date: '',
    purchase_price: '',
    shipping_cost: '',
    grading_cost: '',
    status_name: '',
    notes: '',
    population_when_bought: '',
    current_population: '',
    pop_higher_when_bought: '',
    current_pop_higher: '',
    minor_variety: '',
    die_variety: '',
    mintage: '',
    price_guide_value: '',
    list_price: '',
    pcgs_number: '',
    tags: [],
  });

  // Fetch possible statuses when component mounts
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const statuses = await coins.getPossibleStatuses();
        setPossibleStatuses(statuses);
      } catch (err) {
        console.error('Failed to fetch possible statuses:', err);
        setError('Failed to load status options');
      }
    };
    fetchStatuses();
  }, []);


  const [focusedField, setFocusedField] = useState(null);

  const handleFocus = (e) => {
    const { name } = e.target;
    setFocusedField(name);
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setFocusedField(null);
  };

  const getDisplayValue = (name, value) => {
    // Always return empty string for null/undefined/empty values
    if (!value && value !== 0) return '';
    
    // Convert to string to ensure consistent handling
    const stringValue = String(value);
    
    if (focusedField === name) return stringValue;
    
    if (name === 'price_guide_value' || name === 'purchase_price' || name === 'list_price' || name === 'shipping_cost' || name === 'grading_cost') {
      const num = parseFloat(stringValue);
      if (isNaN(num)) return '';
      return `$${num.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    }
    if (name === 'population_when_bought' || name === 'current_population' || name === 'pop_higher_when_bought' || name === 'current_pop_higher') {
      const num = parseInt(value);
      if (isNaN(num)) return '';
      return num.toLocaleString();
    }
    if (name === 'mintage') {
      if (value === null || value === undefined || value === '') return '';
      // Find the first number in the string
      const match = value.match(/(\d+)/);
      if (!match) return value;
      
      // Get the number and its position
      const number = match[0];
      const index = value.indexOf(number);
      
      try {
        // Format number with commas
        const formattedNumber = parseInt(number.replace(/,/g, '')).toLocaleString('en-US');
        // Return the string with the formatted number
        return value.slice(0, index) + formattedNumber + value.slice(index + number.length);
      } catch (err) {
        // If parsing fails, return the original value
        return value;
      }
    }
    return value;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle different field types
    let finalValue;
    
    if (name === 'population_when_bought' || name === 'current_population' || 
        name === 'pop_higher_when_bought' || name === 'current_pop_higher') {
      // Remove commas and preserve 0 values
      const cleanValue = value.replace(/,/g, '');
      finalValue = cleanValue === '' ? '' : String(parseInt(cleanValue) || 0);
    } else if (name === 'mintage') {
      // For mintage, preserve the full text value
      finalValue = value;
    } else if (name === 'price_guide_value' || name === 'purchase_price' || name === 'list_price' || 
               name === 'shipping_cost' || name === 'grading_cost') {
      // For price fields, allow natural number input and convert empty/zero to empty string
      const numericValue = value.replace(/[^0-9.]/g, '');
      // Only allow one decimal point
      const parts = numericValue.split('.');
      const cleanValue = parts[0] + (parts.length > 1 ? '.' + parts[1] : '');
      finalValue = cleanValue === '' || cleanValue === '0' ? '' : cleanValue;
    } else {
      // For all other text fields, keep empty strings as empty strings
      finalValue = value;
    }

    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
  };

  const validateForm = () => {
    const errors = [];
    if (!formData.user_coin_id) errors.push('User Coin ID is required');
    if (!formData.denomination) errors.push('Denomination is required');
    if (!formData.year) errors.push('Year is required');
    return errors;
  };

  const handleConfirmTransaction = async (showTransactionForm) => {
    if (showTransactionForm) {
      // Show transaction modal
      setShowTransactionModal(true);
      setShowTransactionPrompt(false);
    } else {
      try {
        // Just update the status
        await onSubmit(pendingSubmitData);
        setShowTransactionPrompt(false);
        setPendingSubmitData(null);
      } catch (err) {
        console.error('Form submission error:', err);
        setError('Failed to save coin. Please try again.');
        setShowTransactionPrompt(false);
        setPendingSubmitData(null);
      }
    }
  };

  const hasTransactionFieldsChanged = () => {
    if (!initialFormData?.purchase_transaction_id) return false;
    
    return (
      formData.contact_id !== initialFormData.contact_id ||
      formData.purchase_date !== initialFormData.purchase_date ||
      formData.purchase_price !== initialFormData.purchase_price
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    // Only show delete prompt if seller was explicitly removed
    if (initialFormData?.contact_id && formData.contact_id === null && initialFormData?.purchase_transaction_id) {
      setShowDeleteTransactionPrompt(true);
      return;
    }

    // Check if transaction fields have changed
    if (hasTransactionFieldsChanged()) {
      setShowTransactionUpdateWarning(true);
      return;
    }

    await processSubmit();
  };

  const processSubmit = async (shouldDeleteTransaction = false) => {
    setLoading(true);
    setError(null);
    
    try {
      // If we should delete the transaction, do it first
      if (shouldDeleteTransaction && initialFormData?.purchase_transaction_id) {
        try {
          await transactions.delete(initialFormData.purchase_transaction_id);
        } catch (err) {
          console.error('Failed to delete transaction:', err);
          setError('Failed to delete transaction. Please try again.');
          setLoading(false);
          return;
        }
      }

      const { status_name, tags: selectedTags, ...rest } = formData;
      const selectedStatus = possibleStatuses.find(s => 
        (typeof s === 'object' ? s.name : s) === status_name
      );
      const statusName = typeof selectedStatus === 'object' ? selectedStatus.name : selectedStatus;
      const isSold = statusName?.toLowerCase() === 'sold';

      // Helper function to convert empty strings to null and parse values
      const processField = (value, type = 'string') => {
        if (value === '') return null;
        if (value === null || value === undefined) return null;
        
        switch (type) {
          case 'float':
            return value ? parseFloat(value) : null;
          case 'int':
            return value ? parseInt(value) : null;
          case 'boolean':
            return value || false;
          case 'date':
            return value || null;
          default:
            return value;
        }
      };

      // Field type configurations
      const fieldTypes = {
        purchase_price: 'float',
        shipping_cost: 'float',
        grading_cost: 'float',
        weight: 'float',
        diameter: 'float',
        price_guide_value: 'float',
        list_price: 'float',
        population_when_bought: 'int',
        current_population: 'int',
        pop_higher_when_bought: 'int',
        current_pop_higher: 'int',
        certification_service_id: 'int',
        cac_sticker: 'string',
        purchase_date: 'date'
      };

      const dataToSubmit = Object.entries(rest).reduce((acc, [key, value]) => {
      // Skip special fields that need custom handling
      if (['tags', 'status_name', 'custom_field_values'].includes(key)) {
          return acc;
        }
        
        // Process the field based on its type
        acc[key] = processField(value, fieldTypes[key] || 'string');
        return acc;
      }, {});

      // Add special fields
      dataToSubmit.status = statusName;
      dataToSubmit.tagIds = selectedTags ? selectedTags.map(tag => tag.id) : [];
      // Include images if they exist
      if (formData.images && formData.images.length > 0) {
        dataToSubmit.images = formData.images;
      }
      // Preserve transaction ID unless explicitly deleted
      dataToSubmit.purchase_transaction_id = shouldDeleteTransaction ? null : (formData.purchase_transaction_id || initialFormData?.purchase_transaction_id);

      // Only show transaction prompt if status is changing TO sold
      if (isSold && initialFormData?.status_name?.toLowerCase() !== 'sold') {
        // Store the data to submit and show transaction prompt
        const submitData = {
          ...dataToSubmit,
        };
        setPendingSubmitData(submitData);
        setShowTransactionPrompt(true);
        setLoading(false); // Reset loading since we're showing the prompt
        return;
      }
      
      // If there's no purchase transaction but we have a seller, create one
      if (!formData.purchase_transaction_id && formData.contact_id) {
        try {
          // Create the transaction first
          const newTransaction = await transactions.create({
            contact_id: formData.contact_id,
            transaction_date: formData.purchase_date || null,
            notes: '',
            coin_prices: {
              [coinId]: {
                price: formData.purchase_price ? parseFloat(formData.purchase_price) : 0,
                fees: (formData.shipping_cost ? parseFloat(formData.shipping_cost) : 0) + 
                      (formData.grading_cost ? parseFloat(formData.grading_cost) : 0)
              }
            },
            type: 'purchase'
          });
          
          // Then update the coin with the transaction ID
          const updatedData = {
            ...dataToSubmit,
            purchase_transaction_id: newTransaction.id
          };
          await onSubmit(updatedData);
          return;
        } catch (err) {
          console.error('Failed to create purchase transaction:', err);
          setError('Failed to create purchase transaction. Please try again.');
          setLoading(false);
          return;
        }
      }

      // Update transaction first if needed
      const transactionId = formData.purchase_transaction_id || initialFormData?.purchase_transaction_id;
      if (!shouldDeleteTransaction && transactionId && (
        formData.contact_id !== initialFormData?.contact_id ||
        formData.purchase_date !== initialFormData?.purchase_date ||
        formData.purchase_price !== initialFormData?.purchase_price
      )) {
        try {
          // First get the current transaction data
          const currentTransaction = await transactions.getById(transactionId);
          
          // Update the transaction with all required fields
          await transactions.update(transactionId, {
            contact_id: formData.contact_id,
            transaction_date: formData.purchase_date || currentTransaction.transaction_date || null,
            notes: currentTransaction.notes || '',
            coin_prices: {
              [coinId]: {
                price: formData.purchase_price ? parseFloat(formData.purchase_price) : currentTransaction.coins[0].purchase_price,
                fees: currentTransaction.coins[0].fees || 0
              }
            },
            type: 'purchase'
          });
        } catch (err) {
          console.error('Failed to update transaction contact:', err);
          setError('Failed to update transaction contact. Please try again.');
          setLoading(false);
          return;
        }
      }

      // First update the coin
      await onSubmit(dataToSubmit);
      
      // Then save custom field values separately
      if (Object.keys(customFieldValues).length > 0) {
        await customFields.setValues(coinId, customFieldValues);
      }
      
      setLoading(false);
      window.dispatchEvent(new Event('tagChange'));
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Failed to save coin. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form 
        onSubmit={handleSubmit} 
        className="space-y-6"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
            e.preventDefault();
          }
        }}
      >
        <div className="space-y-4">
          <div className="mb-6 flex items-center justify-between">
          <div>
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center"
            >
              <svg className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              Discard Changes
            </button>
          </div>
          <div>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                loading ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>

            </div>
          </div>
        )}

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
              {/* Basic Information Section */}
        <div className="space-y-4 bg-gray-50 p-6 rounded-lg">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900 border-b pb-2">Basic Information</h3>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-5 gap-4">
              <div className="col-span-2">
                <label htmlFor="user_coin_id" className="block text-sm font-medium text-gray-700 mb-2">
                  Coin ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="user_coin_id"
                  id="user_coin_id"
                  value={formData.user_coin_id}
                  onChange={handleChange}
                  aria-label="User Coin ID"
                  className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label htmlFor="certification_service_id" className="block text-sm font-medium text-gray-700 mb-2">
                  TPG
                </label>
                <select
                  id="certification_service_id"
                  name="certification_service_id"
                  value={formData.certification_service_id}
                  onChange={handleChange}
                  className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                >
                  <option value="">Select a service</option>
                  {services.map(service => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-span-2">
                <label htmlFor="certification_number" className="block text-sm font-medium text-gray-700 mb-2">
                  Cert Number
                </label>
                <div className="flex gap-2">
                  <input
                  type="text"
                  name="certification_number"
                  id="certification_number"
                  value={formData.certification_number}
                  onChange={handleChange}
                  aria-label="Certification Number"
                  autoComplete="off"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!lookupLoading && !isLookupDisabled()) {
                          handleLookup();
                        }
                      }
                    }}
                    className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                  />
                  <button
                    type="button"
                    onClick={handleLookup}
                    disabled={lookupLoading || isLookupDisabled()}
                    title={formData.pcgs_number ? "Refresh PCGS data" : "Refresh certification data"}
                    className={`h-9 w-10 flex items-center justify-center border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                      lookupLoading || isLookupDisabled()
                        ? 'bg-indigo-400 cursor-not-allowed'
                        : 'bg-indigo-600 hover:bg-indigo-700'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                  >
                    {lookupLoading ? (
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <input
                  type="text"
                  name="country"
                  id="country"
                  value={formData.country}
                  onChange={handleChange}
                  autoComplete="country-name"
                  className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-2">
                  Year <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="year"
                  id="year"
                  value={formData.year}
                  onChange={handleChange}
                  autoComplete="off"
                  className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label htmlFor="mint_mark" className="block text-sm font-medium text-gray-700 mb-2">
                  Mint Mark
                </label>
                <input
                  type="text"
                  name="mint_mark"
                  id="mint_mark"
                  value={formData.mint_mark}
                  onChange={handleChange}
                  autoComplete="off"
                  className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label htmlFor="denomination" className="block text-sm font-medium text-gray-700 mb-2">
                  Denomination <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="denomination"
                  id="denomination"
                  value={formData.denomination}
                  onChange={handleChange}
                  autoComplete="off"
                  className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">

              <div>
                <label htmlFor="variety" className="block text-sm font-medium text-gray-700 mb-2">
                  Variety
                </label>
                <input
                  type="text"
                  name="variety"
                  id="variety"
                  value={formData.variety}
                  onChange={handleChange}
                  autoComplete="off"
                  className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label htmlFor="grade" className="block text-sm font-medium text-gray-700 mb-2">
                  Grade
                </label>
                <input
                  type="text"
                  name="grade"
                  id="grade"
                  value={formData.grade}
                  onChange={handleChange}
                  autoComplete="off"
                  className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label htmlFor="cac_sticker" className="block text-sm font-medium text-gray-700 mb-2">
                  CAC Sticker
                </label>
                <select
                  id="cac_sticker"
                  name="cac_sticker"
                  value={formData.cac_sticker}
                  onChange={handleChange}
                  className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                >
                  <option value="none">None</option>
                  <option value="green">Green</option>
                  <option value="gold">Gold</option>
                </select>
              </div>

              <div>
                <label htmlFor="pcgs_number" className="block text-sm font-medium text-gray-700 mb-2">
                  PCGS Number
                </label>
                <input
                  type="text"
                  name="pcgs_number"
                  id="pcgs_number"
                  value={formData.pcgs_number || ''}
                  onChange={handleChange}
                  aria-label="PCGS Number"
                  autoComplete="off"
                  className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="status_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  id="status_name"
                  name="status_name"
                  value={formData.status_name || ''}
                  onChange={handleChange}
                  className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                >
                  {possibleStatuses.length === 0 ? (
                    <option value="">{formData.status_name || 'Loading...'}</option>
                  ) : (
                    possibleStatuses.map(status => (
                      <option 
                        key={typeof status === 'object' ? status.name : status} 
                        value={typeof status === 'object' ? status.name : status}
                        style={status.color ? { backgroundColor: status.color } : {}}
                      >
                        {typeof status === 'object' ? status.name : status}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div className="col-span-2">
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <TagSelector
                  id="tags"
                  selectedTags={formData.tags}
                  onChange={(newTags) => setFormData(prev => ({ ...prev, tags: newTags }))}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>

            {/* Additional Details Section */}
            <div className="space-y-4 bg-gray-50 p-6 rounded-lg">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900 border-b pb-2">Additional Details</h3>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="metal" className="block text-sm font-medium text-gray-700 mb-2">
                Metal
              </label>
              <input
                type="text"
                name="metal"
                id="metal"
                value={formData.metal || ''}
                onChange={handleChange}
                autoComplete="off"
                className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label htmlFor="composition" className="block text-sm font-medium text-gray-700 mb-2">
                Composition (%)
              </label>
                <input
                  type="text"
                  name="composition"
                  id="composition"
                  value={formData.composition || ''}
                  onChange={handleChange}
                  autoComplete="off"
                  className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                />
            </div>

            <div>
              <label htmlFor="weight" className="block text-sm font-medium text-gray-700 mb-2">
                Weight (g)
              </label>
              <input
                type="number"
                name="weight"
                id="weight"
                step="0.0001"
                min="0"
                value={formData.weight || ''}
                onChange={handleChange}
                className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label htmlFor="diameter" className="block text-sm font-medium text-gray-700 mb-2">
                Diameter (mm)
              </label>
              <input
                type="number"
                name="diameter"
                id="diameter"
                step="0.01"
                min="0"
                value={formData.diameter || ''}
                onChange={handleChange}
                className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label htmlFor="edge" className="block text-sm font-medium text-gray-700 mb-2">
                Edge
              </label>
              <input
                type="text"
                name="edge"
                id="edge"
                value={formData.edge || ''}
                onChange={handleChange}
                autoComplete="off"
                className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
              />
            </div>


            <div>
              <label htmlFor="mintage" className="block text-sm font-medium text-gray-700 mb-2">
                Mintage
              </label>
                <input
                  type="text"
                  name="mintage"
                  id="mintage"
                  value={getDisplayValue('mintage', formData.mintage)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  aria-label="Mintage"
                  autoComplete="off"
                  className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                />
            </div>

            <div>
              <label htmlFor="minor_variety" className="block text-sm font-medium text-gray-700 mb-2">
                Minor Variety
              </label>
              <input
                type="text"
                name="minor_variety"
                id="minor_variety"
                value={formData.minor_variety}
                onChange={handleChange}
                autoComplete="off"
                className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label htmlFor="die_variety" className="block text-sm font-medium text-gray-700 mb-2">
                Die Variety
              </label>
              <input
                type="text"
                name="die_variety"
                id="die_variety"
                value={formData.die_variety}
                onChange={handleChange}
                autoComplete="off"
                className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label htmlFor="reference_number" className="block text-sm font-medium text-gray-700 mb-2">
                Ref. #
              </label>
              <input
                type="text"
                name="reference_number"
                id="reference_number"
                value={formData.reference_number || ''}
                onChange={handleChange}
                maxLength={100}
                autoComplete="off"
                className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label htmlFor="assayer" className="block text-sm font-medium text-gray-700 mb-2">
                Assayer
              </label>
              <input
                type="text"
                name="assayer"
                id="assayer"
                value={formData.assayer || ''}
                onChange={handleChange}
                maxLength={100}
                autoComplete="off"
                className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
              />
            </div>

          </div>
        </div>

            {/* Purchase Details Section */}
        <div className="space-y-4 bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Purchase Details</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="purchase_date" className="block text-sm font-medium text-gray-700 mb-1">
                Purchase Date
              </label>
              <input
                type="date"
                name="purchase_date"
                id="purchase_date"
                value={formData.purchase_date || ''}
                onChange={handleChange}
                className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label htmlFor="purchase_price" className="block text-sm font-medium text-gray-700 mb-1">
                Purchase Price
              </label>
              <input
                  type="text"
                  name="purchase_price"
                  id="purchase_price"
                  value={getDisplayValue('purchase_price', formData.purchase_price)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onChange={handleChange}
                className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label htmlFor="shipping_cost" className="block text-sm font-medium text-gray-700 mb-1">
                Shipping Cost
              </label>
              <input
                type="text"
                name="shipping_cost"
                id="shipping_cost"
                value={getDisplayValue('shipping_cost', formData.shipping_cost)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={handleChange}
                className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label htmlFor="grading_cost" className="block text-sm font-medium text-gray-700 mb-1">
                Grading Cost
              </label>
              <input
                type="text"
                name="grading_cost"
                id="grading_cost"
                value={getDisplayValue('grading_cost', formData.grading_cost)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={handleChange}
                className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label htmlFor="seller-search" className="block text-sm font-medium text-gray-700 mb-1">
                Seller
              </label>
              <SellerSelect
                id="seller"
                value={formData.contact_id}
                onChange={(contactId) => setFormData(prev => ({ ...prev, contact_id: contactId }))}
                className="w-full"
              />
            </div>

            <div>
              <label htmlFor="storage_location" className="block text-sm font-medium text-gray-700 mb-1">
                Storage Location
              </label>
              <LocationSelect
                id="storage_location"
                value={formData.storage_location || ''}
                onChange={handleChange}
                className="w-full"
              />
            </div>


          </div>
        </div>

            {/* Population & Price Details Section */}
        <div className="space-y-4 bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Population & Price Details</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-6">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label htmlFor="population_when_bought" className="block text-sm font-medium text-gray-700 mb-1">
                    Population When Bought
                  </label>
                  <input
                    type="text"
                    name="population_when_bought"
                    id="population_when_bought"
                    value={getDisplayValue('population_when_bought', formData.population_when_bought)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label htmlFor="pop_higher_when_bought" className="block text-sm font-medium text-gray-700 mb-1">
                    Pop Higher When Bought
                  </label>
                  <input
                    type="text"
                    name="pop_higher_when_bought"
                    id="pop_higher_when_bought"
                    value={getDisplayValue('pop_higher_when_bought', formData.pop_higher_when_bought)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label htmlFor="current_population" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Population
                  </label>
                  <input
                    type="text"
                    name="current_population"
                    id="current_population"
                    value={getDisplayValue('current_population', formData.current_population)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label htmlFor="current_pop_higher" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Pop Higher
                  </label>
                  <input
                    type="text"
                    name="current_pop_higher"
                    id="current_pop_higher"
                    value={getDisplayValue('current_pop_higher', formData.current_pop_higher)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">

              <div>
                <label htmlFor="price_guide_value" className="block text-sm font-medium text-gray-700 mb-1">
                  Price Guide Value
                </label>
                <input
                  type="text"
                  name="price_guide_value"
                  id="price_guide_value"
                  value={getDisplayValue('price_guide_value', formData.price_guide_value)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label htmlFor="list_price" className="block text-sm font-medium text-gray-700 mb-1">
                  List Price
                </label>
                <input
                  type="text"
                  name="list_price"
                  id="list_price"
                  value={getDisplayValue('list_price', formData.list_price)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>
          </div>
        </div>

            {/* Custom Fields Section */}
            <div className="space-y-4 bg-gray-50 p-6 rounded-lg">
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900 border-b pb-2">Custom Fields</h3>
              </div>
              <CustomFieldsFormSection
                coinId={coinId}
                initialValues={initialFormData?.custom_field_values}
                onChange={setCustomFieldValues}
                className="space-y-4"
              />
            </div>

            {/* Notes & Provenance Section */}
            <div className="col-span-2 space-y-4 bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Details</h3>
              <NotesSection formData={formData} handleChange={handleChange} />
            </div>

          </div>
        </div>
      </div>
      </form>
      {showTransactionPrompt && (
        <TransactionPromptModal
          onConfirm={(showTransactionForm) => {
            handleConfirmTransaction(showTransactionForm);
          }}
          onCancel={() => {
            setShowTransactionPrompt(false);
            setPendingSubmitData(null);
            setLoading(false);
            setError(null);
          }}
        />
      )}

      {showDeleteTransactionPrompt && (
        <Transition appear show={true} as={Fragment}>
          <Dialog 
            as="div" 
            className="relative z-50" 
            onClose={() => setShowDeleteTransactionPrompt(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      Delete Purchase Transaction?
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Removing the seller will delete the associated purchase transaction. This action cannot be undone.
                      </p>
                    </div>

                    <div className="mt-4 flex justify-end space-x-3">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                        onClick={() => {
                          setShowDeleteTransactionPrompt(false);
                          processSubmit(true);
                        }}
                      >
                        Delete Transaction
                      </button>
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                        onClick={() => {
                          setShowDeleteTransactionPrompt(false);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}

      {showCertUpdateModal && (
        <CertUpdateModal
          isOpen={true}
          onClose={() => {
            setShowCertUpdateModal(false);
            setPendingCertUpdate(null);
            setLookupLoading(false);
          }}
          changes={pendingCertUpdate.changedFields}
          onApply={(selectedChanges) => {
            applyLookupUpdate(
              pendingCertUpdate.response,
              pendingCertUpdate.isPCGSNumber,
              selectedChanges
            );
            setShowCertUpdateModal(false);
            setPendingCertUpdate(null);
          }}
        />
      )}

      {showTransactionModal && (
        <SaleTransactionModal
          isOpen={true}
          onClose={() => {
            setShowTransactionModal(false);
            setPendingSubmitData(null);
          }}
          onSubmit={async (transactionData) => {
            try {
              // Create transaction
              await transactions.create({
                ...transactionData,
                coin_id: coinId
              });

              // Submit form data
              await onSubmit(pendingSubmitData);
              
              // Reset state
              setShowTransactionModal(false);
              setPendingSubmitData(null);
            } catch (err) {
              console.error('Failed to create transaction:', err);
              setError('Failed to create transaction. Please try again later.');
            }
          }}
          coins={[formData]}
        />
      )}

      {showTransactionUpdateWarning && (
        <TransactionUpdateWarningModal
          isOpen={true}
          onClose={() => {
            setShowTransactionUpdateWarning(false);
            setLoading(false);
          }}
          onConfirm={async () => {
            setShowTransactionUpdateWarning(false);
            await processSubmit();
          }}
        />
      )}
    </>
  );
};

export default EditCoinForm;
